import { Injectable } from '@angular/core';
import { HttpRequest, HttpResponse } from '@angular/common/http';
import { StorageService } from './storage.service';
import { Util } from '../util';
import { OfflineService } from './offline.service';
import { indexedDbStore } from './indexedDB.service';

export interface RequestCacheEntry {
  url: string;
  response: HttpResponse<any>;
  lastRead: number;
  arrayBuffer?: boolean;
}

// #docregion request-cache
export abstract class RequestCache {
  abstract get(req: HttpRequest<any>): HttpResponse<any> | undefined;
  abstract put(req: HttpRequest<any>, response: HttpResponse<any>): void;
}
// #enddocregion request-cache

// 15min
const maxAge = 900000; // 86400000; // maximum cache age (ms) = 24h

@Injectable({
  providedIn: 'root'
})
export class RequestCacheWithMap {

  constructor(
    private storageService: StorageService,
    private offlineService: OfflineService,
  ) { }

  async get(req: HttpRequest<any>): Promise<HttpResponse<any> | undefined> {
    return await this.getFromUrl(req.urlWithParams);
  }

  public async getFromUrlOffline(url: string){
    const offline: indexedDbStore = await this.offlineService.getItem(url);

    if (Util.isNullOrUndefined(offline)) {
      return undefined;
    }

    return offline.json;
  }

  public async getFromUrl(url: string, arrayBuffer?: boolean): Promise<HttpResponse<any>> {
    let cached: RequestCacheEntry = await this.storageService.getItem<RequestCacheEntry>(url);

    if (Util.isNullOrUndefined(cached)) {
      const offline: indexedDbStore = await this.offlineService.getItem(url);

      if (Util.isNullOrUndefined(offline)) {
        return undefined;
      }

      cached = offline.json;

    }

    //const isExpired = cached.lastRead < (Date.now() - maxAge);
    const isExpired = false;
    const expired = isExpired ? 'expired ' : '';
    console.log(`Found ${expired}cached response for "${url}".`);
    // this.messenger.add(`Found ${expired}cached response for "${url}".`);

    let responseResult = isExpired ? undefined : cached.response.body;

    if (arrayBuffer === true && isExpired === false) {
      if (cached?.arrayBuffer === true) {
        responseResult = await this.cache2ArrayBuffer(cached);
      } else {
        if (Util.isJson(responseResult)) {
          responseResult = JSON.parse(responseResult);
        } else {
          responseResult = cached.response.body;
        }
      }
    }

    if (isExpired) {
      this.removeUrl(url);
    }

    return responseResult;
  }

  async put(req: HttpRequest<any>, response: HttpResponse<any>, offline?: boolean): Promise<void> {
    await this.putFromUrl(req.urlWithParams, response, offline);
  }

  async putArrayBuffer(req: HttpRequest<any>, response: HttpResponse<any>, offline?: boolean): Promise<void> {
    await this.putFromUrl(req.urlWithParams, response, offline, true);
  }

  async putFromUrl(url: string, response: HttpResponse<any>, offline?: boolean, arrayBuffer?: boolean): Promise<void> {
    console.log(`Caching response from "${url}".`);
    // this.messenger.add(`Caching response from "${url}".`);
    let newEntry: RequestCacheEntry = null;

    if (arrayBuffer === true) {
      console.log('Converting array buffer to cache')
      newEntry = this.arrayBuffer2Cache(response);
    } else {
      newEntry = { url, response, lastRead: Date.now() };
    }

    if (offline) {
      try {
        console.log('Saving offline')
        await this.offlineService.storeAsync(url, newEntry);
      } catch (error) {
        console.log('Offline save error', error);
      }
    } else {
      await this.storageService.storeAsync(url, newEntry);
    }

    return Promise.resolve();
    // remove expired cache entries
    // const expired = Date.now() - maxAge;
    // this.cache.forEach(entry => {
    //   if (entry.lastRead < expired) {
    //     this.cache.delete(entry.url);
    //   }
    // });
    // this.messenger.add(`Request cache size: ${this.cache.size}.`);
  }

  async putFromUrlToOffline(url: string, response: any){
    console.log(`Caching response from "${url}".`);
    try {
      await this.offlineService.storeAsync(url, response);
    } catch (error) {
      console.log('Offline save error', error);
    }
  }

  async removeUrl(url: string): Promise<void> {
    await this.offlineService.removeItem(url);
    this.storageService.removeItem(url);
  }

  private arrayBuffer2Cache(response: HttpResponse<any>): RequestCacheEntry {
    const isArrBuf = Util.isAbv(response.body);

    const newResponse = new HttpResponse<any>({
      // body: isArrBuf ? Util.ab2str(response.body) : JSON.stringify(response.body),
      body: isArrBuf ? Util.arrayBufferToBase64(response.body) : JSON.stringify(response.body),
      // headers: response.headers,
      // status: response.status,
      // statusText: response.statusText,
      // url: response.url
    });

    return {
      url: newResponse.url,
      arrayBuffer: isArrBuf,
      response: newResponse,
      lastRead: Date.now()
    };
  }

  private async cache2ArrayBuffer(request: RequestCacheEntry): Promise<HttpResponse<any>> {
    // const ab: ArrayBuffer = Util.str2ab(request.response.body);
    const ab: ArrayBuffer = Util.base64ToArrayBuffer(request.response.body);

    const newResponse = new HttpResponse<ArrayBuffer>({
      body: ab,
      headers: request.response.headers,
      status: request.response.status,
      statusText: request.response.statusText,
      url: request.response.url
    });
    return newResponse;
  }
}
